import { Input, Skeleton } from "@gotts.ai/ui";
import { formatUnits } from "viem";

import { DEVENV_TOKEN_PRICES } from "../lib/constants";
import {
  formatTokenAmount,
  formatTokenDisplay,
  formatUsd,
  truncateDecimals,
} from "../lib/format";

interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  symbol: string;
  /** Raw balance (bigint) */
  balance?: bigint;
  decimals?: number;
  disabled?: boolean;
  /** Whether balances are still being fetched */
  loading?: boolean;
  testId?: string;
}

const PERCENTAGES = [25, 50, 75, 100] as const;

function computePercentageAmount(
  balance: bigint,
  decimals: number,
  pct: number,
): string {
  const scaled = (balance * BigInt(pct)) / 100n;
  const raw = formatUnits(scaled, decimals);
  return formatTokenDisplay(truncateDecimals(raw, 6)).full;
}

export function AmountInput({
  value,
  onChange,
  symbol,
  balance,
  decimals = 18,
  disabled,
  loading,
  testId,
}: AmountInputProps) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          placeholder="0.0"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          disabled={disabled}
          data-testid={testId}
          className="octa-sm !border-0"
        />
      </div>
      {/* Balance display: skeleton while loading, value when loaded */}
      {loading && balance === undefined ? (
        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="text-[12px] text-[var(--color-text-muted)]">
            Balance:
          </span>
          <Skeleton className="h-3.5 w-24" />
        </div>
      ) : (
        balance !== undefined &&
        (() => {
          const balanceNum = Number(balance) / 10 ** decimals;
          const usdValue = balanceNum * (DEVENV_TOKEN_PRICES[symbol] ?? 0);
          return (
            <div className="mt-1.5 text-[12px] text-[var(--color-text-muted)]">
              Balance: {formatTokenAmount(balance, decimals).display} {symbol}
              {usdValue > 0 && (
                <span className="ml-1 opacity-70">
                  ({"\u2248"} {formatUsd(usdValue)})
                </span>
              )}
            </div>
          );
        })()
      )}
      {/* Percentage buttons: skeleton while loading, buttons when loaded */}
      {loading && balance === undefined ? (
        <div className="mt-1.5 flex justify-end gap-1.5">
          {PERCENTAGES.map((pct) => (
            <Skeleton key={pct} className="h-6 w-9" />
          ))}
        </div>
      ) : (
        balance !== undefined &&
        balance > 0n && (
          <div className="mt-1.5 flex justify-end gap-1.5">
            {PERCENTAGES.map((pct) => (
              <button
                key={pct}
                type="button"
                className={
                  pct === 100
                    ? "btn-percentage-pill-max"
                    : "btn-percentage-pill"
                }
                onClick={() => {
                  onChange(computePercentageAmount(balance, decimals, pct));
                }}
                disabled={disabled}
                data-testid={
                  testId ? `${testId}-pct-${String(pct)}` : undefined
                }
              >
                {pct === 100 ? "Max" : `${String(pct)}%`}
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}
